import liff from '@line/liff'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { ConfigProvider, Layout, message, Spin } from 'antd'
import type { AxiosError } from 'axios'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useSSEEvent } from '@/context/SSEContext'
import { useLayoutConfigContext } from '@/providers/layoutConfig.provider'
import { queries } from '@/queries'
import { API, COMMONS } from '@/utils'

const { Content } = Layout

const ClientMain = (props) => {
	const { children } = props
	const { liffId } = useParams()
	const [searchParams] = useSearchParams()

	const navigate = useNavigate()
	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
	const queryClient = useQueryClient()

	const personalInfoRef = useRef()

	const [accessToken, setAccessToken] = useState<string | undefined>(undefined)
	const [personalInfo, setPersonalInfo] = useState({})

	const layoutContext = useLayoutConfigContext()
	const { logo, favicon, storePic, publicSettings } = layoutContext

	const personalInfoQuery = useQuery({
		queryKey: [API.QUERY_KEY_CLIENT_PERSONAL_INFO, accessToken],
		queryFn: () => API.CLIENT_GET_PERSONAL_INFO(accessToken),
		enabled: !!accessToken,
	})

	useEffect(() => {
		if (personalInfoQuery.isSuccess && personalInfoQuery.data) {
			const response = personalInfoQuery.data
			if (isMountedRef.current) {
				setPersonalInfo(response?.data || {})
			}
		}
	}, [personalInfoQuery.isSuccess, personalInfoQuery.data])

	useEffect(() => {
		if (personalInfoQuery.isError) {
			const error = personalInfoQuery.error as AxiosError
			if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				})
			} else {
				message.error({
					content: COMMONS.ERROR_SYSTEM_MSG,
					key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
				})
			}
		}
	}, [personalInfoQuery.isError, personalInfoQuery.error])

	useEffect(() => {
		const redirectToLogin = () => {
			const currentPath = window.location.pathname
			const isMembershipPage =
				currentPath.includes('/membership')
			const isEventsPage = currentPath.includes('/events')
			const isRegistrationsPage = currentPath.includes('/registrations')

			let query = ''
			if (isMembershipPage) {
				query = '?membership=true'
			} else if (isEventsPage) {
				query = '?events=true'
			} else if (isRegistrationsPage) {
				query = '?registrations=true'
			} else {
				query = `?liff.state=${encodeURIComponent(currentPath)}`
			}

			const route = liffId
				? `${COMMONS.CLIENT_LOGIN_ROUTE}/${liffId}${query}`
				: `${COMMONS.CLIENT_LOGIN_ROUTE}${query}`
			navigate(route)
		}

		try {
			const token = liff.getAccessToken()
			if (token) {
				setAccessToken(token)
			} else {
				redirectToLogin()
			}
		} catch (err) {
			redirectToLogin()
		}

		// eslint-disable-next-line
	}, [liffId])

	useEffect(() => {
		let link = document.querySelector("link[rel~='icon']")

		if (!link) {
			link = document.createElement('link')
			link.rel = 'icon'
			document.getElementsByTagName('head')[0].appendChild(link)
		}

		if (favicon) {
			link.href = API.SETTINGS_UPLOADS_URL + favicon
		}
	}, [favicon])

	useEffect(() => {
		personalInfoRef.current = personalInfo
	}, [personalInfo])

	const systemSettingsHandler = useCallback((response) => {
		queryClient.invalidateQueries({
			queryKey: [API.QUERY_KEY_PUBLIC_SETTINGS],
		})

		if (response !== undefined && Object.keys(response).length !== 0) {
			if (
				response?.keys &&
				Array.isArray(response.keys) &&
				(response.keys.includes(API.SETTINGS_KEY_BOOK_LIMIT_ENABLED) ||
					response.keys.includes(API.SETTINGS_KEY_BOOK_LIMIT_DAY) ||
					response.keys.includes(API.SETTINGS_KEY_BOOK_LIMIT_HOUR) ||
					response.keys.includes(API.SETTINGS_KEY_BOOK_LIMIT_MINUTE))
			) {
				// Removed invalidation of category and occasion detail queries
			}
		}
	}, [])
	const memberHandler = useCallback(
		(response) => {
			if (response?.memberId === personalInfoRef.current?.memberId && response.data) {
				queryClient.setQueriesData(
					{ queryKey: [API.QUERY_KEY_CLIENT_PERSONAL_INFO] },
					(oldData: any) => {
						if (!oldData?.data) return oldData
						return {
							...oldData,
							data: { ...oldData.data, ...response.data },
						}
					},
				)
			}
		},
		[queryClient],
	)

	const faviconHandler = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queries.settings.favicon.queryKey,
		})
	}, [])
	const logoHandler = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: queries.settings.logo.queryKey })
	}, [])
	const storePicHandler = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queries.settings.storePic.queryKey,
		})
	}, [])

	useSSEEvent(API.SSE_FAVICON, faviconHandler)
	useSSEEvent(API.SSE_LOGO, logoHandler)
	useSSEEvent(API.SSE_STORE_PIC, storePicHandler)
	useSSEEvent(API.SSE_SYSTEM_SETTING, systemSettingsHandler)
	useSSEEvent(API.SSE_MEMBER, memberHandler)

	useEffect(() => {
		if (searchParams.get('registrations')) {
			localStorage.setItem('registrations', searchParams.get('registrations'))
		}
	}, [])

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
	}, [])

	useEffect(() => {
		const root = document.documentElement;
		const originalSize = root.style.fontSize;
		root.style.fontSize = "24px";
		root.classList.add("liff-mode");

		return () => {
			root.style.fontSize = originalSize;
			root.classList.remove("liff-mode");
		};
	}, []);

	const childrenWithProps = React.Children.map(children, (element) =>
		React.cloneElement(element, {
			logo: logo,
			storePic: storePic,
			publicSettings: publicSettings,
			accessToken: accessToken,
			setAccessToken: setAccessToken,
			personalInfo: personalInfo,
			isMountedRef: isMountedRef,
		}),
	)

	return (
		<>
			<Helmet>
				{publicSettings?.TITLE?.valueString ? (
					<title>{publicSettings?.TITLE?.valueString}</title>
				) : (
					''
				)}
			</Helmet>
			<ConfigProvider
				theme={{
					token: {
						colorPrimary: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLink: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLinkActive: publicSettings?.PRIMARY_COLOR?.valueString,
						colorLinkHover: publicSettings?.PRIMARY_COLOR?.valueString,
						fontSize: 24,
						controlHeight: 60,
					},
				}}
			>
				<div className='flex flex-col w-full min-h-full'>
					<Spin
						spinning={
							useIsFetching({
								fetchStatus: 'fetching',
							}) > 0
								? true
								: false
						}
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 1001,
						}}
					>
						<Layout className='min-h-screen bg-white'>
							<Content>
								<div className='w-full m-auto'>{childrenWithProps}</div>
							</Content>
							{/* <ClientBottomMenuBarComponent
								publicSettings={publicSettings}
								personalInfo={personalInfo}
								accessToken={accessToken}
							/> */}
						</Layout>
					</Spin>
				</div>
			</ConfigProvider>
		</>
	)
}

export default ClientMain
